import React from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Camera,
  MapPin,
  Calendar,
  Crown,
  ArrowRight,
  Shield,
  Zap,
  PenLine,
  Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const JournalGalleryPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <SEOHead
        title="Walking Journal — Wanderloop+"
        description="Keep a private walking journal. Log notes, photos, and memories from every walk you complete."
        noindex
      />
      <Header />

      {/* Hero Illustration Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-amber-200/20 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-orange-200/20 rounded-full blur-xl"></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 text-center">
          <div className="mb-12">
            <div className="relative inline-block">
              <div className="w-80 h-60 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl shadow-2xl mx-auto relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100 to-orange-100 opacity-70"></div>

                {/* Journal lines */}
                <div className="absolute inset-x-8 top-16 space-y-3">
                  <div className="h-0.5 bg-amber-300/40"></div>
                  <div className="h-0.5 bg-amber-300/40"></div>
                  <div className="h-0.5 bg-amber-300/40"></div>
                  <div className="h-0.5 bg-amber-300/40"></div>
                  <div className="h-0.5 bg-amber-300/40"></div>
                </div>

                {/* Journal icon */}
                <div className="absolute top-10 left-1/2 transform -translate-x-1/2">
                  <div className="w-20 h-20 bg-gradient-to-r from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <BookOpen className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-6 h-6 bg-amber-400 rounded-full shadow-lg animate-bounce">
                  <PenLine className="w-4 h-4 text-white m-1" />
                </div>
                <div className="absolute bottom-12 left-8 w-5 h-5 bg-orange-400 rounded-full shadow-lg animate-bounce delay-300">
                  <Camera className="w-3 h-3 text-white m-1" />
                </div>
              </div>

              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full p-3 shadow-xl">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Your Private Walking Journal <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-orange-500">
              Every Walk, Remembered
            </span>
          </h1>

          <p className="text-xl text-gray-700 mb-12 max-w-3xl mx-auto leading-relaxed">
            Walking Journal is an exclusive Wanderloop+ feature — capture your thoughts,<br />
            photos, and memories from every walk you take.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-black text-gray-900 mb-8">
                With Walking Journal, You Can:
              </h2>

              <div className="space-y-6">
                {[
                  {
                    icon: <PenLine className="h-6 w-6" />,
                    title: "Log notes after every walk",
                    description: "Capture thoughts, tips, and observations while they're fresh"
                  },
                  {
                    icon: <Camera className="h-6 w-6" />,
                    title: "Attach photos and memories",
                    description: "Build a visual archive of your walking journey"
                  },
                  {
                    icon: <Calendar className="h-6 w-6" />,
                    title: "Track your walking history",
                    description: "See when and where you've walked over time"
                  },
                  {
                    icon: <MapPin className="h-6 w-6" />,
                    title: "Link entries to specific walks",
                    description: "Connect your journal to Wanderloop routes you've completed"
                  }
                ].map((feature, index) => (
                  <div key={index} className="flex items-start gap-4 group">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-400 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110">
                      {feature.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview Card */}
            <div>
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 shadow-xl">
                <CardHeader>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-orange-500 rounded-lg flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-xl text-gray-900">Walking Journal</h3>
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Wanderloop+ Feature</Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  {/* Mock journal entries */}
                  {[
                    { date: 'April 10, 2026', walk: 'Silver Lake Reservoir Loop', note: 'Beautiful golden hour light. Saw three herons by the water.' },
                    { date: 'April 7, 2026', walk: 'Griffith Observatory Trail', note: 'Clear day — could see all the way to Catalina.' },
                    { date: 'April 3, 2026', walk: 'Venice Canals & Boardwalk', note: 'Best tacos at the stand on Rose Ave. Must return.' },
                  ].map((entry, index) => (
                    <div key={index} className="bg-white/80 backdrop-blur-sm rounded-lg p-4 border border-amber-200">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-3 w-3 text-amber-600" />
                        <span className="text-xs text-amber-600 font-medium">{entry.date}</span>
                      </div>
                      <p className="text-sm font-semibold text-gray-900">{entry.walk}</p>
                      <p className="text-xs text-gray-600 italic mt-1">"{entry.note}"</p>
                    </div>
                  ))}

                  <div className="bg-gradient-to-r from-amber-100 to-orange-100 p-4 rounded-lg border-l-4 border-amber-400">
                    <div className="flex items-center gap-2 mb-2">
                      <Image className="h-5 w-5 text-amber-600" />
                      <span className="font-bold text-amber-800">Photo Memories</span>
                    </div>
                    <p className="text-sm text-gray-700">Attach photos to any journal entry and build a visual timeline of your walks.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjIwIiByPSIzIi8+PC9nPjwvZz48L3N2Zz4=')]"></div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-8">
            Start Journaling Your Walks
          </h2>

          <p className="text-xl mb-12 text-blue-100 max-w-2xl mx-auto">
            Walking Journal is available exclusively for Wanderloop+ founding members.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
            <Link to="/membership">
              <Button
                size="lg"
                className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-black px-12 py-4 text-xl shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                <Crown className="mr-3 h-6 w-6" />
                Become a Founding Member
                <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-8 text-sm text-blue-100">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span>Cancel anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <span>Founding rate locked in</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JournalGalleryPage;
